import { useSuspenseQuery } from '@tanstack/react-query'

import { API_ENDPOINTS } from '../../../common/apiEndpoints'
import {
  ModuleConfig,
  ModuleConfigList,
  moduleConfigListSchema,
} from '../../../common/validations/moduleConfigs'
import {
  ResidentAppModuleConfigSchema,
  type ResidentAppModuleConfig,
} from '../../../common/validations/residentAppModuleConfig'
import { get } from '../../api/apiClient'
import { useCurrentContract } from '../useCurrentContract/useCurrentContract'

export const useAPIGetModuleConfigSuspense = (currentContractId: string) =>
  useSuspenseQuery({
    queryKey: [API_ENDPOINTS.MODULE_CONFIG, currentContractId],
    queryFn: () =>
      get(
        API_ENDPOINTS.MODULE_CONFIG.replace(':contractUuid', currentContractId),
        moduleConfigListSchema
      ),
  })

export type { ResidentAppModuleConfig }

const getSettingOrDefault = (
  data: ModuleConfigList,
  moduleName: string
): ModuleConfig | undefined => {
  return data.find((m) => m.name === moduleName)
}

const parseConfigFromData = (
  data: ModuleConfigList | null
): ResidentAppModuleConfig => {
  if (data) {
    const defectNoticeConfig = getSettingOrDefault(data, 'defectNotice')
    const parkingSpotsConfig = getSettingOrDefault(data, 'parkingSpots')
    const storagesConfig = getSettingOrDefault(data, 'storages')
    const saunaConfig = getSettingOrDefault(data, 'sauna')
    const gymConfig = getSettingOrDefault(data, 'gym')
    const gymReservationsConfig = getSettingOrDefault(data, 'gymReservations')
    const terminateContractShowingConfig = getSettingOrDefault(
      data,
      'terminateContractShowing'
    )
    const terminationDateMustBeMonthEndConfig = getSettingOrDefault(
      data,
      'terminationDateMustBeMonthEnd'
    )
    const laundryReservationConfig = getSettingOrDefault(
      data,
      'laundryReservation'
    )
    const commonAreaReservationsConfig = getSettingOrDefault(
      data,
      'commonAreaReservations'
    )

    const configuration: ResidentAppModuleConfig = {
      contractTerminationOnly:
        getSettingOrDefault(data, 'contractTerminationOnly')?.isEnabled ??
        false,
      defectNotice: {
        enabled: defectNoticeConfig?.isEnabled ?? true,
        customDefectNoticeLinkUrl: defectNoticeConfig?.metadata
          ? (defectNoticeConfig.metadata.url as string) || undefined
          : undefined,
        feedback: defectNoticeConfig?.metadata
          ? ((defectNoticeConfig.metadata.feedback as boolean) ?? true)
          : true,
        question: defectNoticeConfig?.metadata
          ? ((defectNoticeConfig.metadata.question as boolean) ?? true)
          : true,
      },
      messaging: getSettingOrDefault(data, 'messaging')?.isEnabled ?? false,
      extendedMessaging:
        getSettingOrDefault(data, 'extendedMessaging')?.isEnabled ?? false,
      contractModification:
        getSettingOrDefault(data, 'contractModification')?.isEnabled ?? false,
      parkingSpots: {
        enabled: parkingSpotsConfig?.isEnabled ?? false,
        showQueue: parkingSpotsConfig?.metadata
          ? ((parkingSpotsConfig.metadata.showQueue as boolean) ?? true)
          : true,
        contractDocumentLink: parkingSpotsConfig?.metadata
          ? (parkingSpotsConfig.metadata.contractDocumentLink as string) ||
            undefined
          : undefined,
        termsOfUseDocumentLink: parkingSpotsConfig?.metadata
          ? (parkingSpotsConfig.metadata.termsOfUseDocumentLink as string) ||
            undefined
          : undefined,
      },
      storages: {
        enabled: storagesConfig?.isEnabled ?? false,
        contractDocumentLink: storagesConfig?.metadata
          ? (storagesConfig.metadata.contractDocumentLink as string) ||
            undefined
          : undefined,
        termsOfUseDocumentLink: storagesConfig?.metadata
          ? (storagesConfig.metadata.termsOfUseDocumentLink as string) ||
            undefined
          : undefined,
      },
      sauna: {
        enabled: saunaConfig?.isEnabled ?? false,
        contractDocumentLink: saunaConfig?.metadata
          ? (saunaConfig.metadata.contractDocumentLink as string) || undefined
          : undefined,
        termsOfUseDocumentLink: saunaConfig?.metadata
          ? (saunaConfig.metadata.termsOfUseDocumentLink as string) || undefined
          : undefined,
      },
      gym: {
        enabled: gymConfig?.isEnabled ?? false,
        contractDocumentLink: gymConfig?.metadata
          ? (gymConfig.metadata.contractDocumentLink as string) || undefined
          : undefined,
        termsOfUseDocumentLink: gymConfig?.metadata
          ? (gymConfig.metadata.termsOfUseDocumentLink as string) || undefined
          : undefined,
      },
      gymReservations: {
        enabled: gymReservationsConfig?.isEnabled ?? false,
        contractDocumentLink: gymReservationsConfig?.metadata
          ? (gymReservationsConfig.metadata.contractDocumentLink as string) ||
            undefined
          : undefined,
        termsOfUseDocumentLink: gymReservationsConfig?.metadata
          ? (gymReservationsConfig.metadata.termsOfUseDocumentLink as string) ||
            undefined
          : undefined,
      },
      laundryReservation: {
        enabled: laundryReservationConfig?.isEnabled ?? false,
        contractDocumentLink:
          (laundryReservationConfig?.metadata
            ?.contractDocumentLink as string) ?? null,
        termsOfUseDocumentLink:
          (laundryReservationConfig?.metadata
            ?.termsOfUseDocumentLink as string) ?? null,
      },
      commonAreaReservations: {
        enabled: commonAreaReservationsConfig?.isEnabled ?? false,
        contractDocumentLink: commonAreaReservationsConfig?.metadata
          ? (commonAreaReservationsConfig.metadata
              .contractDocumentLink as string) || undefined
          : undefined,
        termsOfUseDocumentLink: commonAreaReservationsConfig?.metadata
          ? (commonAreaReservationsConfig.metadata
              .termsOfUseDocumentLink as string) || undefined
          : undefined,
      },
      terminateContract: {
        showingVisible: terminateContractShowingConfig?.isEnabled ?? true,
        showingDefault: terminateContractShowingConfig?.metadata
          ? (terminateContractShowingConfig.metadata.default as
              | 'tenant'
              | 'company')
          : 'tenant',
        endDateLastDayOfMonth:
          terminationDateMustBeMonthEndConfig?.isEnabled ?? true,
      },
      reservationConfirmationEnabled:
        getSettingOrDefault(data, 'reservationConfirmationEnabled')
          ?.isEnabled ?? false,
      userInfo: {
        showCommunicationPreferences:
          getSettingOrDefault(data, 'userInfoCommunicationPreferences')
            ?.isEnabled ?? true,
        showElectronicServicesConsent:
          getSettingOrDefault(data, 'userInfoElectronicServicesConsent')
            ?.isEnabled ?? false,
      },
    }
    return ResidentAppModuleConfigSchema.parse(configuration)
  }
  return ResidentAppModuleConfigSchema.parse({})
}

export const useModuleConfig = () => {
  const { currentContractId } = useCurrentContract()
  const { data } = useAPIGetModuleConfigSuspense(currentContractId)
  return parseConfigFromData(data)
}
